import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle, Download, Info } from 'lucide-react';
import axios from 'axios';
import '../../styles/LeadManagement.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BulkUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState('');

  // Helper function to get the appropriate token
  const getAuthToken = () => {
    const rbacSessionStr = sessionStorage.getItem('rbac_session');
    if (rbacSessionStr) {
      try {
        const rbacSession = JSON.parse(rbacSessionStr);
        if (rbacSession.token) {
          return rbacSession.token;
        }
      } catch (err) {
        console.error('Error parsing RBAC session:', err);
      }
    }
    return localStorage.getItem('voicebot_access_token') || localStorage.getItem('admin_voicebot_access_token');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv' // .csv
      ];
      
      if (!allowedTypes.includes(file.type) && 
          !file.name.endsWith('.xlsx') && 
          !file.name.endsWith('.xls') && 
          !file.name.endsWith('.csv')) {
        setError('Please select a valid Excel file (.xlsx, .xls, or .csv)');
        setSelectedFile(null);
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        setSelectedFile(null);
        return;
      }

      setError('');
      setSelectedFile(file);
      setUploadResults(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');
    setUploadResults(null);

    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${API_BASE_URL}/leads/bulk-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setUploadResults(response.data.results);
        
        // Notify parent component to refresh leads
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create a comprehensive sample CSV template with proper headers and examples
    const template = `name,phone,email,country_code,notes,source,interest_level
John Doe,2345678901,john.doe@example.com,+1,Interested in premium package,manual,8
Jane Smith,9876543210,jane.smith@example.com,+91,Follow up next week,manual,7
Bob Johnson,7700900123,bob.johnson@example.com,+44,Hot lead - called twice,manual,9
Sarah Williams,450123456,sarah.w@example.com,+61,Needs product demo,manual,6
Ahmed Ali,501234567,ahmed@example.com,+971,Corporate client inquiry,manual,8
Maria Garcia,611234567,maria@example.com,+34,Referred by existing client,manual,9
Li Wei,13912345678,liwei@example.com,+86,Bulk order request,manual,7
Raj Kumar,9988776655,raj@example.com,+91,Budget constraints mentioned,manual,5
Emma Brown,6123456789,emma@example.com,+65,Very interested in trial,manual,9
David Lee,416123456,david@example.com,+1,Requested callback tomorrow,manual,6`;

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_bulk_upload_template.csv';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setUploadResults(null);
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div 
        className="modal-content lead-detail-modal" 
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Upload size={24} />
            Bulk Upload Leads from Excel
          </h3>
          <button 
            onClick={handleClose} 
            disabled={uploading}
            style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}
          >
            &times;
          </button>
        </div>

        <div className="modal-body">
          {/* Instructions */}
          <div 
            style={{
              padding: '12px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              marginBottom: '16px',
              display: 'flex',
              gap: '8px'
            }}
          >
            <Info size={20} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '14px', color: '#1e40af' }}>
              <strong>Instructions:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>Download the template below - it has sample data to guide you</li>
                <li>Fill in your lead data (you can delete sample rows)</li>
                <li><strong>Required columns:</strong> name, phone</li>
                <li><strong>Optional columns:</strong> email, country_code, notes, source, interest_level</li>
                <li><strong>Country code:</strong> Use format +XX (e.g., +1, +91, +44) - defaults to +91 if not provided</li>
                <li><strong>Phone number:</strong> Enter only digits without country code (e.g., 9876543210)</li>
                <li><strong>Note:</strong> Phone will be saved as country_code + phone (e.g., +919876543210)</li>
                <li>Supported formats: .xlsx, .xls, .csv</li>
                <li>Maximum file size: 10MB</li>
              </ul>
            </div>
          </div>

          {/* Download Template Button */}
          <button
            onClick={downloadTemplate}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '20px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
          >
            <Download size={18} />
            Download Sample Template (10 example leads included)
          </button>

          {/* File Upload */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>
              Select Excel File
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              disabled={uploading}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px dashed #cbd5e1',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            />
            {selectedFile && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#059669' }}>
                <FileSpreadsheet size={16} />
                <span style={{ fontSize: '14px' }}>{selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)</span>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div 
              style={{
                padding: '12px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '6px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#c33'
              }}
            >
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Upload Results */}
          {uploadResults && (
            <div 
              style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                marginBottom: '16px'
              }}
            >
              <h4 style={{ margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#374151' }}>
                <CheckCircle size={20} style={{ color: '#10b981' }} />
                Upload Results
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>{uploadResults.total}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Rows</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>{uploadResults.successful}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Successful</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>{uploadResults.skipped}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Skipped (Duplicates)</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>{uploadResults.failed}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Failed</div>
                </div>
              </div>

              {/* Errors */}
              {uploadResults.errors && uploadResults.errors.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <strong style={{ color: '#ef4444' }}>Errors:</strong>
                  <div style={{ maxHeight: '150px', overflowY: 'auto', marginTop: '8px' }}>
                    {uploadResults.errors.map((err, idx) => (
                      <div 
                        key={idx} 
                        style={{ 
                          padding: '6px 8px', 
                          backgroundColor: '#fef2f2', 
                          borderLeft: '3px solid #ef4444',
                          marginBottom: '6px',
                          fontSize: '13px'
                        }}
                      >
                        Row {err.row}: {err.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              style={{
                flex: 1,
                padding: '12px 24px',
                backgroundColor: (!selectedFile || uploading) ? '#cbd5e1' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: (!selectedFile || uploading) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Upload size={18} />
              {uploading ? 'Uploading...' : 'Upload Leads'}
            </button>
            
            <button
              onClick={handleClose}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '12px 24px',
                backgroundColor: 'white',
                color: '#64748b',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              {uploadResults ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
